<template>
	<div class="flex flex-col gap-8">
		<FormGroup :label="$t('forms.asset_transfer.asset_transfer_ownership_form.new_owner_company')">
			<Dropdown
				v-model="form.new_company_id"
				:options="supplierOptions"
				option-label="name"
				option-value="id"
				:disabled="(disabled || isDisabled) || !form.company_id"
				show-clear
				:placeholder="$t('forms.asset_transfer.asset_transfer_ownership_form.select_component_supplier')"
			/>
		</FormGroup>

		<FormGroup :label="$t('forms.asset_transfer.asset_transfer_ownership_form.notes')" :error-text="form.errors?.notes">
			<Textarea
				class="w-full mt-1"
				rows="5"
				v-model="note"
				:placeholder="$t('forms.asset_transfer.asset_transfer_ownership_form.notes_placeholder')"
			/>
		</FormGroup>

	</div>

	<template v-if="showActions || isDeleted">
		<div class="flex justify-end mt-8 space-x-4">
			<Button v-if="showReset" link @click="$emit('on-reset', form)" :label="$t('forms.common.reset')" :disabled="form.processing"/>
			<Button v-if="!isDeleted" @click="confirmTransfer" :label="$t('forms.asset_transfer.asset_transfer_ownership_form.submit_button')" :disabled="form.processing"/>
		</div>
	</template>
</template>

<script>
import FormGroup from "@/Components/FormGroup.vue";
import { mapStores } from "pinia";
import { useCompaniesStore } from "@/stores/companies.js";
import { useAssetsStore } from "@/stores/assets.js";

export default {
	name: "AssetTransferOwnershipForm",
	components: {FormGroup},
	emits: ['on-save', 'on-reset'],
	props: {
		form: {
			type: Object,
			required: true,
		},
		showActions: {
			type: Boolean,
			default: true,
		},
		showReset: {
			type: Boolean,
			default: false,
		},
		// Explicit asset id 
		assetId: {
			type: [Number, String],
			required: false,
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		isDisabled: {
			type: Boolean,
			default: false,
		},
		isDeleted: {
			type: Boolean,
			default: false,
		}
	},
	data() {
		return {
			supplierOptions: [],
			note: '',
		};
	},
	computed: {
		...mapStores(
		useCompaniesStore,
		useAssetsStore
    ),
	},
	methods: {
		confirmTransfer() {
			const newId = this.form?.new_company_id;
			if (!newId) {
				this.form.errors = {
					...(this.form.errors || {}),
					new_company_id: this.$t('forms.asset_transfer.asset_transfer_ownership_form.select_new_owner_company'),
				};
				return;
			}
			// Find selected company name for display
			const selected = (this.supplierOptions || []).find(o => o.id === newId);
			const newName = selected?.name || this.$t('common.company');
			const prevName = this.form?.company?.name || this.$t('common.company');

			this.$confirm.require({
				group: 'confirm',
				header: this.$t('forms.asset_transfer.asset_transfer_ownership_form.confirm_header'),
				message: this.$t('forms.asset_transfer.asset_transfer_ownership_form.confirm_message', { prev: prevName, next: newName }),
				accept: () => this.performTransfer(),
				reject: () => this.$toast?.add({
					severity: 'info',
					summary: this.$t('common.cancelled'),
					detail: this.$t('forms.asset_transfer.asset_transfer_ownership_form.transfer_cancelled'),
					life: 3000
				})
			});
		},
		async performTransfer() {
			try {
				const assetId = this.assetId ?? this.form?.id ?? this.form?.asset_id ?? this.$route?.params?.id;
				if (!assetId) {
					// inline error if we cannot resolve asset id
					this.form.errors = {
						...(this.form.errors || {}),
						id: this.$t('forms.asset_transfer.asset_transfer_ownership_form.missing_asset_id'),
					};
					return;
				}
				const payload = {
					new_company_id: this.form.new_company_id,
					previous_company_id: this.form.company_id,
					notes: this.note || undefined,
				};
				await this.assetsStore.transferOwnership(assetId, payload);
				this.$emit('on-save', this.form);
			} catch (error) {
				console.error(error);
			}
		}
	},
	watch: {
		'form.company_id'(val) {
			this.supplierOptions = [];
			if (val) {
				this.companiesStore.indexComponentSuppliers(val, { fields: ['id','name'], per_page: 100 })
					.then((res) => { this.supplierOptions = res?.data ?? []; })
					.catch(() => { this.supplierOptions = []; });
			}
		}
	},
	mounted() {
	    if (this.form.company_id) {
	        this.companiesStore.indexComponentSuppliers(this.form.company_id, { fields: ['id','name'], per_page: 100 })
	            .then((res) => { this.supplierOptions = res?.data ?? []; })
	            .catch(() => { this.supplierOptions = []; });
	    }
	}
}
</script>

<style scoped>

</style>
